// IMPORT REACT
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react'

// IMPORT UI COMPONENTS
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  GetGrowthChart,
  GetGrowthChartTokenDetail,
  GetOpdDetailsForReport
} from '../../services/TokenService';
import { useLocation } from 'react-router-dom';
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import html2pdf from "html2pdf.js";
import HeightChart from './HeightChart';
import WeightChart from './WeightChart';
import BMIChart from './BMIChart';
import HeadCircumferenceChart from './HeadCircumferenceChart';
import PriscriptionHeader from '../../pages/Report/Priscription/PriscriptionHeader';
import PatientDetails from '../../pages/Report/Priscription/PatientDetails';
import PriscriptionFooter from '../../pages/Report/Priscription/PriscriptionFooter';
import { format } from 'date-fns';
import { PreferencesContext } from '../../PreferenceContext';
import { AlertType } from '../../services/models/AlertType';
import { PAGE_SIZE } from '../../services/common/const';

interface Parameter {
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  startDate: Date;
  endDate: Date;
}

const parameters: Parameter = {
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  startDate: new Date(),
  endDate: new Date(),
};

interface BranchDetails {
  consumerId: number;
  organizationId: number;
  branchId: number;
  patientId: number;
  opdId: number;
  departmentId: number;
  specialityId: number;
  dateOfBirth: Date | null;
}

interface GrowthChartData {
  growthChartSeriesColl: Array<any>;
  heightChartZoneColl: Array<any>;
  weightChartZoneColl: Array<any>;
  bmiChartZoneColl: Array<any>;
  hcChartZoneColl: Array<any>;
}

const initHeightData: GrowthChartData = {
  growthChartSeriesColl: [],
  heightChartZoneColl: [],
  weightChartZoneColl: [],
  bmiChartZoneColl: [],
  hcChartZoneColl: [],
};

const GrowthChartReport = () => {

  const initialBranchDetails: BranchDetails = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    patientId: 0,
    opdId: 0,
    departmentId: 0,
    specialityId: 0,
    dateOfBirth: null,
  };

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const [branchDetails, setBranchDetails] = React.useState<BranchDetails>(initialBranchDetails);
  const [open, setOpen] = React.useState(false);
  const [zoomLevel, setZoomLevel] = React.useState(1);
  const [growthChartData, setGrowthChartData] = React.useState(initHeightData);
  const htmlprintRef = useRef<HTMLDivElement>(null);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [ageCountData, setAgeCountData] = React.useState(-1);
  const [param, setParam] = React.useState(parameters);
  const [detailedOpd, setDetailedOpd] = React.useState(null);
  const [isDownload, setIsDownload] = React.useState(false);

  const zoomOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let token = urlParams.get("t");
  let currentToken: string = '';

  if (token !== null && token !== undefined) {
    currentToken = token;
  }

  //CLOSE FOR DIALOG
  const handleClose = (event: any, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    setOpen(false);
  };

  const handleZoomChange = (event: any) => {
    setZoomLevel(parseFloat(event.target.value));
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => {
      const nextZoom = zoomOptions.find((z) => z > prev) || prev;
      return nextZoom;
    });
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => {
      const nextZoom =
        [...zoomOptions].reverse().find((z) => z < prev) || prev;
      return nextZoom;
    });
  };

  const handleReportDownload = async () => {
    setIsDownload(true);
    await new Promise(r => setTimeout(r, 300));

    const element = htmlprintRef.current;
    if (!element) return;

    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 2,
          scrollY: 0,
          width: 794,
          minHeight: 1123,
        },
        filename: `document.pdf`,
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: {
          mode: ["css", "legacy"],
        },
      })
      .from(element)
      .save()
      .then(() => setIsDownload(false));
  };

  const getGrowthChartTokenDetail = async () => {
    try {
      let tokenData: any = null;
      const params = new URLSearchParams();
      params.append("token", token || "");

      const result: any = await GetGrowthChartTokenDetail(params, currentToken);

      if (result?.data?.data) {
        const data = result.data.data;

        tokenData = {
          consumerId: data.consumerId ?? 0,
          organizationId: data.organizationId ?? 0,
          branchId: data.branchId ?? 0,
          patientId: data.patientId ?? 0,
          opdId: data.opdId ?? 0,
          departmentId: data.departmentId ?? 0,
          specialityId: data.specialityId ?? 0,
          dateOfBirth: data.dateOfBirth || null,
        };

        setBranchDetails(tokenData);
      }
      return tokenData;
    } catch (e) {
      console.error("Error fetching branch details:", e);
    }
  };

  const fetchGrowthChartDetail = async (
    patientId: string,
    dateOfBirth: Date | null,
  ) => {
    try {
      let params = new URLSearchParams();

      params.append("patientId", patientId);
      if (dateOfBirth) {
        params.append("startDate", format(dateOfBirth, "yyyy-MM-dd"));
      }
      if (param?.endDate) {
        params.append("endDate", format(param.endDate, "yyyy-MM-dd"));
      }

      const result: any = await GetGrowthChart(params, currentToken);

      if (result?.data?.data?.growthChartSeriesColl) {
        const growthChartData: GrowthChartData = result?.data?.data;
        setGrowthChartData(growthChartData);
      }

      if (userContext?.memberDetail?.age === 0) {
        setAgeCountData(0);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
    setIsLoading(false);
  }

  const fetchDetailedOpdforReport = async (
    opdId: string,
    consumerId: string,
    organizationId: string,
    branchId: string,
    departmentId: string,
    specialityId: string,
    patientId: string,
  ) => {
    try {
      let params = new URLSearchParams();
      let reportData = null;
      params.append("opdId", opdId);
      params.append("consumerId", consumerId);
      params.append("branchId", branchId);
      params.append("organizationId", organizationId);
      params.append("departmentId", departmentId);
      params.append("specialityId", specialityId);
      params.append("patientId", patientId);
      const result: any = await GetOpdDetailsForReport(params, currentToken);
      if (result?.data?.data) {
        reportData = result?.data?.data;
        setDetailedOpd(reportData);
      }
      return reportData;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const tokenData = await getGrowthChartTokenDetail();
      if (tokenData?.patientId || 0) {
        await fetchGrowthChartDetail(
          tokenData.patientId || 0,
          tokenData.dateOfBirth || null,
        );
        await fetchDetailedOpdforReport(
          tokenData.opdId || 0,
          tokenData.consumerId || 0,
          tokenData.organizationId || 0,
          tokenData.branchId || 0,
          tokenData.departmentId || 0,
          tokenData.specialityId || 0,
          tokenData.patientId || 0
        );
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    setOpen(true);
    fetchData();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "4px",
          height: "100vh",
          padding: "0px",
          margin: "0px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #ddd",
          padding: "4px 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0}
            sx={{ padding: 0, margin: 0 }}
          >
            <Select
              value={zoomLevel}
              onChange={handleZoomChange}
              sx={{
                mx: 1,
                minWidth: 70,
                border: "none",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                backgroundColor: "transparent",
                padding: 0,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1px solid #c7c5c5",
                    borderRadius: "4px",
                  },
                },
              }}
            >
              {zoomOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Typography
                    sx={{
                      fontSize: "13px !important",
                    }}
                  >
                    {(option * 100).toFixed(0)}%
                  </Typography>
                </MenuItem>
              ))}
            </Select>

            <Box display="flex" gap={1}>
              <Tooltip title="Zoom Out" placement="top">
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.25}
                  sx={{ padding: 0 }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom In" placement="top">
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 2}
                  sx={{ padding: 0 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Tooltip title="Download PDF" placement="top">
              <IconButton
                aria-label="Download PDF"
                onClick={handleReportDownload}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          height: "100%",
          padding: "16px !important",
          fontFamily: "Arial, sans-serif",
          backgroundColor: "#f1f3f5",
          boxSizing: "border-box",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width: "max-content",
            border: "1px solid #ddd",
            boxShadow: "4px rgba(0, 0, 0, 0.2)",
            borderRadius: "2px",
            padding: "30px 50px",
            fontFamily: "Arial, sans-serif",
            backgroundColor: "white",
            boxSizing: "border-box",
            transform: `scale(${zoomLevel})`,
            transformOrigin: "center",
            transition: "transform 0.2s ease-in-out",
            margin: "0 auto",
          }}
        >
          <Box ref={htmlprintRef} sx={{ width: "794px" }}>
            {isDownload ? (
              <>
                {(() => {
                  const growthSeries = growthChartData?.growthChartSeriesColl ?? [];
                  const hasHeight = growthSeries.some(item => item?.height > 0);
                  const hasWeight = growthSeries.some(item => item?.weight > 0);
                  const hasBMI = growthSeries.some(item => item?.bmi > 0);
                  const hasHead = growthSeries.some(item => item?.headCircumference > 0);

                  const hasAnyChart: string[] = [];
                  if (hasHeight) hasAnyChart.push("height");
                  if (hasWeight) hasAnyChart.push("weight");
                  if (hasBMI) hasAnyChart.push("bmi");
                  if (hasHead) hasAnyChart.push("head");

                  if (!hasAnyChart) {
                    return (
                      <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                        width: "100%"
                      }}>
                        No chart data found
                      </div>
                    );
                  }

                  return hasAnyChart.map((chart, index) => {
                    const isLast = index === hasAnyChart.length - 1;

                    return (
                      <table style={{ width: '100%', pageBreakInside: 'avoid' }}>
                        <thead>
                          <tr>
                            <td>
                              {detailedOpd?.businessInformation?.headerEnable && (
                                <Box className="header">
                                  <PriscriptionHeader headerData={detailedOpd?.businessInformation} />
                                </Box>
                              )}
                              <Box sx={{ borderBottom: "1px solid #c7c5c5" }}>
                                <PatientDetails patientData={detailedOpd} />
                              </Box>
                            </td>
                          </tr>
                        </thead>
                        <tbody style={{ height: '100%', verticalAlign: 'top' }}>
                          <tr style={{ height: '100%' }}>
                            <td>
                              <>
                                {chart === "height" && (
                                  <Box sx={{ marginBottom: '120px' }}>
                                    <HeightChart
                                      motherHeight={growthChartData?.growthChartSeriesColl?.[0]?.motherHeight ?? 0}
                                      fatherHeight={growthChartData?.growthChartSeriesColl?.[0]?.fatherHeight ?? 0}
                                      heightList={growthChartData?.growthChartSeriesColl}
                                      intervalType={growthChartData?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                      heightChartZoneData={growthChartData?.heightChartZoneColl}
                                      isMobileScreen={false}
                                      isDownload={true}
                                    />
                                  </Box>
                                )}
                                {chart === "weight" && (
                                  <Box sx={{ marginBottom: '130px' }}>
                                    <WeightChart
                                      weightList={growthChartData?.growthChartSeriesColl}
                                      intervalType={growthChartData?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                      weightChartZoneData={growthChartData?.weightChartZoneColl}
                                      isMobileScreen={false}
                                      isDownload={true}
                                    />
                                  </Box>
                                )}
                                {chart === "bmi" && (
                                  <Box sx={{ marginBottom: '120px' }}>
                                    <BMIChart
                                      bmiList={growthChartData?.growthChartSeriesColl}
                                      intervalType={growthChartData?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                      bmiChartZoneData={growthChartData?.bmiChartZoneColl}
                                      isMobileScreen={false}
                                      isDownload={true}
                                    />
                                  </Box>
                                )}
                                {chart === "head" && (
                                  <Box sx={{ marginBottom: '60px' }}>
                                    <HeadCircumferenceChart
                                      headCircumferenceList={growthChartData?.growthChartSeriesColl}
                                      intervalType={growthChartData?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                      headCircumferenceChartZoneData={growthChartData?.hcChartZoneColl}
                                      isMobileScreen={false}
                                      isDownload={true}
                                    />
                                  </Box>
                                )}
                              </>
                            </td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr>
                            <td>
                              <Box className="footer">
                                <PriscriptionFooter footerData={detailedOpd} selectedSections={{ FOOTER: true }} />
                              </Box>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    );
                  });
                })()}
              </>
            ) : (
              <table style={{ width: '100%', pageBreakInside: 'avoid' }}>
                <thead>
                  <tr>
                    <td>
                      {detailedOpd?.businessInformation?.headerEnable && (
                        <Box className="header">
                          <PriscriptionHeader headerData={detailedOpd?.businessInformation} />
                        </Box>
                      )}
                      <Box sx={{ borderBottom: "1px solid #c7c5c5" }}>
                        <PatientDetails patientData={detailedOpd} />
                      </Box>
                    </td>
                  </tr>
                </thead>
                <tbody style={{ height: '100%', verticalAlign: 'top' }}>
                  <tr style={{ height: '100%' }}>
                    <td>
                      <Box className="content">
                        {(() => {
                          const growthSeries = growthChartData?.growthChartSeriesColl ?? [];
                          const hasHeight = growthSeries.some(item => item?.height > 0);
                          const hasWeight = growthSeries.some(item => item?.weight > 0);
                          const hasBMI = growthSeries.some(item => item?.bmi > 0);
                          const hasHead = growthSeries.some(item => item?.headCircumference > 0);
                          const hasAnyChart = hasHeight || hasWeight || hasBMI || hasHead;

                          if (!hasAnyChart) {
                            return (
                              <div style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100px",
                                width: "100%"
                              }}>
                                No chart data found
                              </div>
                            );
                          }

                          return (
                            <>
                              {hasHeight && (
                                <Box sx={{ marginBottom: '40px' }}>
                                  <HeightChart
                                    motherHeight={growthChartData?.growthChartSeriesColl?.[0]?.motherHeight ?? 0}
                                    fatherHeight={growthChartData?.growthChartSeriesColl?.[0]?.fatherHeight ?? 0}
                                    heightList={growthChartData?.growthChartSeriesColl}
                                    intervalType={growthChartData?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                    heightChartZoneData={growthChartData?.heightChartZoneColl}
                                    isMobileScreen={false}
                                    isDownload={true}
                                  />
                                </Box>
                              )}
                              {hasWeight && (
                                <Box sx={{ marginBottom: '40px' }}>
                                  <WeightChart
                                    weightList={growthChartData?.growthChartSeriesColl}
                                    intervalType={growthChartData?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                    weightChartZoneData={growthChartData?.weightChartZoneColl}
                                    isMobileScreen={false}
                                    isDownload={true}
                                  />
                                </Box>
                              )}
                              {hasBMI && (
                                <Box sx={{ marginBottom: '40px' }}>
                                  <BMIChart
                                    bmiList={growthChartData?.growthChartSeriesColl}
                                    intervalType={growthChartData?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                    bmiChartZoneData={growthChartData?.bmiChartZoneColl}
                                    isMobileScreen={false}
                                    isDownload={true}
                                  />
                                </Box>
                              )}
                              {hasHead && (
                                <Box>
                                  <HeadCircumferenceChart
                                    headCircumferenceList={growthChartData?.growthChartSeriesColl}
                                    intervalType={growthChartData?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                    headCircumferenceChartZoneData={growthChartData?.hcChartZoneColl}
                                    isMobileScreen={false}
                                    isDownload={true}
                                  />
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td>
                      <Box className="footer">
                        <PriscriptionFooter footerData={detailedOpd} selectedSections={{ FOOTER: true }} />
                      </Box>
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default GrowthChartReport
